import { useEffect, type ReactNode, type ButtonHTMLAttributes } from 'react';
import { useDaten } from '../daten/kontext';

const PFADE: Record<string, string> = {
  plus: 'M12 5v14M5 12h14',
  x: 'M18 6 6 18M6 6l12 12',
  links: 'm15 18-6-6 6-6',
  rechts: 'm9 18 6-6-6-6',
  haken: 'M20 6 9 17l-5-5',
  warnung: 'M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  person: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  online: 'M2 4h20v12H2zM8 20h8M12 16v4',
  raum: 'M3 21h18M5 21V4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v17M14 12h.01',
  kalender: 'M3 5h18v16H3zM16 3v4M8 3v4M3 10h18',
  zauber: 'm15 4-1 3 3-1M3 21 14 10m4-6 1-2 1 2 2 1-2 1-1 2-1-2-2-1zM7 3l.5 1.5L9 5l-1.5.5L7 7l-.5-1.5L5 5l1.5-.5z',
  stempel: 'M5 22h14M19 18H5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2zM9 16V9.5a3 3 0 1 1 6 0V16',
  muell: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6',
  senden: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
  glocke: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.9 1.9 0 0 0 3.4 0',
  hand: 'M18 11V6a2 2 0 0 0-4 0M14 10V4a2 2 0 0 0-4 0v2M10 10.5V6a2 2 0 0 0-4 0v8M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.9-6-2.4L2.4 16a2 2 0 0 1 2.8-2.8L7 15',
  zurueck: 'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5',
  liste: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  stift: 'M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  stern: 'm12 2 3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z',
  link: 'M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7',
};
export function Icon({ name, groesse = 18, titel }: { name: keyof typeof PFADE | string; groesse?: number; titel?: string }) {
  return (
    <svg width={groesse} height={groesse} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden={titel ? undefined : true} role={titel ? 'img' : undefined} className="icon">
      {titel && <title>{titel}</title>}
      <path d={PFADE[name] ?? ''} />
    </svg>
  );
}

type KnopfArt = 'primaer' | 'sekundaer' | 'leise' | 'gefahr';
export function Knopf({ art = 'sekundaer', icon, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { art?: KnopfArt; icon?: string }) {
  return (
    <button type="button" {...rest} className={`knopf knopf-${art} ${rest.className ?? ''}`}>
      {icon && <Icon name={icon} />}{children && <span>{children}</span>}
    </button>
  );
}

export function Seitenpanel({ titel, untertitel, offen, schliessen, children, fuss }: {
  titel: ReactNode; untertitel?: ReactNode; offen: boolean; schliessen: () => void; children: ReactNode; fuss?: ReactNode;
}) {
  useEffect(() => {
    if (!offen) return;
    const t = (e: KeyboardEvent) => { if (e.key === 'Escape') schliessen(); };
    window.addEventListener('keydown', t); return () => window.removeEventListener('keydown', t);
  }, [offen, schliessen]);
  if (!offen) return null;
  return (
    <div className="panel-huelle" onMouseDown={e => { if (e.target === e.currentTarget) schliessen(); }}>
      <aside className="panel" role="dialog" aria-modal="true" aria-label={typeof titel === 'string' ? titel : undefined}>
        <header className="panel-kopf">
          <div><h2>{titel}</h2>{untertitel && <div className="panel-unter">{untertitel}</div>}</div>
          <Knopf art="leise" icon="x" onClick={schliessen} aria-label="Schließen" />
        </header>
        <div className="panel-inhalt">{children}</div>
        {fuss && <footer className="panel-fuss">{fuss}</footer>}
      </aside>
    </div>
  );
}

export function Feld({ label, hinweis, children }: { label: string; hinweis?: ReactNode; children: ReactNode }) {
  return <label className="feld"><span className="feld-label">{label}</span>{children}{hinweis && <span className="feld-hinweis">{hinweis}</span>}</label>;
}

export function Fortschritt({ wert, max, freigegeben }: { wert: number; max: number; freigegeben?: number }) {
  const p = max > 0 ? Math.min(100, (wert / max) * 100) : 0;
  const f = max > 0 && freigegeben !== undefined ? Math.min(100, (freigegeben / max) * 100) : 0;
  const ueber = wert > max && max > 0;
  return (
    <div className={`fortschritt ${ueber ? 'ueber' : ''}`} role="progressbar" aria-valuenow={wert} aria-valuemax={max}>
      <div className="fortschritt-geplant" style={{ width: `${p}%` }} />
      {freigegeben !== undefined && <div className="fortschritt-frei" style={{ width: `${f}%` }} />}
    </div>
  );
}

export function Kuerzel({ text, farbe, klein }: { text: string; farbe: string; klein?: boolean }) {
  return <span className={`kuerzel ${klein ? 'klein' : ''}`} style={{ background: farbe }}>{text}</span>;
}

export function Meldungen() {
  const { meldungen } = useDaten();
  return (
    <div className="meldungen" aria-live="polite">
      {meldungen.slice(-3).map(m => (
        <div key={m.id} className={`meldung meldung-${m.art}`}>
          <Icon name={m.art === 'fehler' ? 'warnung' : m.art === 'ok' ? 'haken' : 'glocke'} />
          <span>{m.text}</span>
        </div>
      ))}
    </div>
  );
}

export function Leer({ icon = 'liste', titel, text, aktion }: { icon?: string; titel: string; text?: string; aktion?: ReactNode }) {
  return <div className="leer"><Icon name={icon} groesse={28} /><strong>{titel}</strong>{text && <p>{text}</p>}{aktion}</div>;
}

export function dozentName(d?: { anrede: string | null; vorname: string; nachname: string } | null) {
  if (!d) return '';
  return [d.anrede, d.vorname, d.nachname].filter(Boolean).join(' ');
}

export function RueckfrageDialog() {
  const { rueckfrage } = useDaten();
  useEffect(() => {
    if (!rueckfrage) return;
    const t = (e: KeyboardEvent) => { if (e.key === 'Escape') rueckfrage.antwort(false); };
    window.addEventListener('keydown', t); return () => window.removeEventListener('keydown', t);
  }, [rueckfrage]);
  if (!rueckfrage) return null;
  return (
    <div className="rueckfrage-huelle" role="alertdialog" aria-modal="true" aria-label="Rückfrage">
      <div className="rueckfrage">
        <p>{rueckfrage.text}</p>
        <div className="knopfleiste">
          <Knopf art={rueckfrage.gefahr ? 'gefahr' : 'primaer'} autoFocus onClick={() => rueckfrage.antwort(true)}>{rueckfrage.ok}</Knopf>
          <Knopf art="leise" onClick={() => rueckfrage.antwort(false)}>Abbrechen</Knopf>
        </div>
      </div>
    </div>
  );
}
